import { useEffect, useRef } from 'react';
import maplibregl, { type LngLatBounds } from 'maplibre-gl';
import ReactDOMServer from 'react-dom/server';
import { DecodePath } from '../tools/ItineraryDecoder';
import type { UnifiedItineraryDTO, UnifiedLegDTO } from '../types/Itinerary';
import type { SVGComponentProps } from '../types/Svgs';

import Car from '../image/icons/map/tripsDetails/CarIcon';
import Train from '../image/icons/map/tripsDetails/TrainIcon';
import Tram from '../image/icons/map/tripsDetails/TramIcon';
import Bus from '../image/icons/map/tripsDetails/BusIcon';
import Walk from '../image/icons/map/tripsDetails/WalkingIcon';
import Bicycle from '../image/icons/map/tripsDetails/BicycleIcon';
import Donkey from '../image/icons/map/tripsDetails/RentBikeIcon';
import ArrivalPoint from '../image/icons/map/tripsDetails/DestinationIcon';
import BlaBlaDaily from '../image/icons/map/tripsDetails/BlaBlaDaily';
import HeLeman from '../image/icons/map/tripsDetails/HeLemanIcon';
import { useEphemeral } from '../store/EphemeralContext';

// Palette de couleurs selon le mode
const colorMap: Record<string, string> = {
  mycar: '#7d1515',
  car: '#7d1515',
  subway: '#259c2a',
  train: '#259c2a',
  rail: '#259c2a',
  tram: '#146c9c',
  bicycle: '#ba551e',
  bus: '#ba551e',
  coach: '#ba551e',
  walk: '#026625',
  arrivalPoint: '#112e66',
  rentBike: '#ba551e',
  blabladaily: '#ba551e',
  heLeman: '#7d1515'
};

// Équivalent du composant SwitchIcon, mais on renvoie directement le JSX
// qu'on transformera en HTML pour MapLibre.
function getModeIconJSX(mode: string, props: SVGComponentProps) {
  switch (mode) {
    case 'mycar':
    case 'car':
      return <Car {...props} />;
    case 'train':
    case 'rail':
      return <Train {...props} />;
    case 'tram':
      return <Tram {...props} />;
    case 'subway':
      return <Tram {...props} />;
    case 'bus':
    case 'coach':
      return <Bus {...props} />;
    case 'walk':
      return <Walk {...props} />;
    case 'arrivalPoint':
      return <ArrivalPoint {...props} />;
    case 'bicycle':
      return <Bicycle {...props} />;
    case 'rentBike':
      return <Donkey {...props} />;
    case 'blabladaily':
      return <BlaBlaDaily {...props} />;
    case 'heLeman':
      return <HeLeman {...props} />;
    default:
      return null;
  }
}

interface UseMapItineraryProps {
  itinerary: UnifiedItineraryDTO | null;
  tripListHeight: number; // En pourcentage, ou pixel, à adapter
}

/**
 * Hook qui trace un itinéraire sur la carte MapLibre et place des marqueurs
 * en fonction des legs d'un UnifiedItineraryDTO.
 */
export function useMapItinerary({ itinerary, tripListHeight }: UseMapItineraryProps) {
  const { state: { map }, setActiveRouteGeometry } = useEphemeral();
  // On stocke ici la liste des "choses" qu’on va ajouter dans la map :
  //   - Ids de sources / layers
  //   - Instances de Marker
  const mapElementsRef = useRef<
  Array<{
    sourceId?: string;
    layerId?: string;
    marker?: maplibregl.Marker;
  }>
  >([]);

  useEffect(() => {
    if (map === undefined || itinerary == null) return;

    // Récupère la hauteur réelle du container (si besoin)
    // ou calcule-la (par exemple "tripListHeight" étant un pourcentage).
    const container = document.getElementById('tripList_id');
    const containerHeight = container != null ? container.offsetHeight : (tripListHeight * window.innerHeight) / 100;

    const topContainerHeight = 160; // marge haute
    const bottomContainerHeight = containerHeight;

    // Nettoie la map (supprime les layers & markers précédents)
    const clearMapElements = () => {
      mapElementsRef.current.forEach((elem) => {
        if (elem.layerId !== undefined && map.getLayer(elem.layerId) != null) {
          map.removeLayer(elem.layerId);
        }
        if (elem.sourceId !== undefined && map.getSource(elem.sourceId) != null) {
          map.removeSource(elem.sourceId);
        }
        if (elem.marker != null) {
          elem.marker.remove();
        }
      });
      mapElementsRef.current = [];
    };

    // Avant d’ajouter quoi que ce soit, on supprime tout
    clearMapElements();

    // Pour calculer le bounding box global
    let bounds: LngLatBounds | null = null;

    itinerary.legs.forEach((leg: UnifiedLegDTO, index: number) => {
      if (leg.geometry === undefined || leg.mode === undefined) return;

      // Décodage du tableau de points (LatLng) côté Leaflet
      // => On va devoir inverser en [lng, lat] pour MapLibre
      const decodedPath = DecodePath(leg.geometry, leg.geometryType);
      if (decodedPath.length === 0) return;

      const polylineColor = colorMap[leg.mode] ?? 'blue';

      // Crée la source GeoJSON (LineString)
      const lineSourceId = `lineSource_${index}_${Date.now()}`;
      const lineLayerId = `lineLayer_${index}_${Date.now()}`;

      const geojsonLine: GeoJSON.GeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: decodedPath.map((latlng) => [latlng.lng, latlng.lat])
        },
        properties: {}
      };

      map.addSource(lineSourceId, {
        type: 'geojson',
        data: geojsonLine
      });

      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: lineSourceId,
        paint: {
          'line-color': polylineColor,
          'line-width': 5
        }
      });

      // Stocke la référence pour pouvoir nettoyer plus tard
      mapElementsRef.current.push({ sourceId: lineSourceId, layerId: lineLayerId });

      // Met à jour le bounds pour chaque point
      decodedPath.forEach((point) => {
        if (bounds == null) {
          bounds = new maplibregl.LngLatBounds([point.lng, point.lat], [point.lng, point.lat]);
        } else {
          bounds.extend([point.lng, point.lat]);
        }
      });

      // Ajout du marqueur de début
      const iconSize = [20, 20];
      const startMarkerHtml = ReactDOMServer.renderToStaticMarkup(getModeIconJSX(leg.mode, { size: iconSize, primaryColor: polylineColor }));
      const startMarkerEl = document.createElement('div');
      startMarkerEl.innerHTML = startMarkerHtml;
      const startMarker = new maplibregl.Marker({ element: startMarkerEl }).setLngLat([decodedPath[0].lng, decodedPath[0].lat]).addTo(map);
      mapElementsRef.current.push({ marker: startMarker });

      // Ajout du marqueur d’arrivée (uniquement si plusieurs points)
      if (decodedPath.length > 1) {
        const endMarkerHtml = ReactDOMServer.renderToStaticMarkup(
          getModeIconJSX('arrivalPoint', { size: iconSize, primaryColor: '#112e66', secondaryColor: '#fff' })
        );
        const endMarkerEl = document.createElement('div');
        endMarkerEl.innerHTML = endMarkerHtml;
        const endMarker = new maplibregl.Marker({ element: endMarkerEl })
          .setLngLat([decodedPath[decodedPath.length - 1].lng, decodedPath[decodedPath.length - 1].lat])
          .addTo(map);
        mapElementsRef.current.push({ marker: endMarker });
      }
    });

    // Mettre à jour l'état activeRouteGeometry pour le LineFollower
    if (itinerary.legs.length > 0) {
      // Collecter toutes les polylines des différents segments
      const polylines = itinerary.legs
        .filter(leg => leg.geometry !== undefined && leg.mode !== undefined)
        .map(leg => {
          const decodedPath = DecodePath(leg.geometry ?? '', leg.geometryType);
          const segmentId = Math.random().toString(36).substring(7);

          // Convertir le mode de transport en type compatible avec ActiveRouteGeometry
          let routeType: 'car' | 'walk' | 'transit' | 'bike' | 'other' = 'other';

          switch (leg.mode) {
            case 'mycar':
            case 'car':
            case 'heLeman':
              routeType = 'car';
              break;
            case 'walk':
              routeType = 'walk';
              break;
            case 'bus':
            case 'train':
            case 'rail':
            case 'tram':
            case 'subway':
            case 'coach':
              routeType = 'transit';
              break;
            case 'bicycle':
            case 'rentBike':
              routeType = 'bike';
              break;
            default:
              routeType = 'other';
          }

          return {
            id: `${leg.mode}_${segmentId}`,
            type: routeType,
            coordinates: decodedPath.map(point => [point.lng, point.lat] as [number, number])
          };
        });

      if (polylines.length > 0) {
        // Créer un ActiveRouteGeometry pour le LineFollower
        setActiveRouteGeometry({
          id: 'transit_route',
          polylines
        });
      }
    } else {
      // Si l'itinéraire n'a pas de segments, réinitialiser l'activeRouteGeometry
      setActiveRouteGeometry(null);
    }

    // Faire un fitBounds sur tous les points
    if (bounds !== null) {
      map.fitBounds(bounds, {
        padding: {
          top: topContainerHeight,
          bottom: bottomContainerHeight + 100,
          left: 0,
          right: 0
        },
        maxZoom: 15,
        duration: 1000 // animation
      });

      // L'équivalent de Leaflet's invalidateSize :
      // on force le resize après un léger délai
      setTimeout(() => {
        map.resize();
      }, 300);
    }

    // Nettoyage au démontage ou au changement d’itinéraire
    return () => {
      clearMapElements();
    };
  }, [map, itinerary, tripListHeight, setActiveRouteGeometry]);
}
